import type { BannerActionsProps, BannerCommonProps, BannerProps } from '@neo4j-ndl/react';
import { Banner } from '@neo4j-ndl/react';
import { APP_SCOPE } from '@nx/constants';
import { createLogger } from '@nx/logger/legacy';
import { addNotification, consoleApi, getApiError, getErrorMessage, logout } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import classNames from 'classnames';

const logger = createLogger(APP_SCOPE.framework);

const ErrorBanner = ({
  description,
  className,
  title = 'API Error',
  hasIcon,
  actions,
}: {
  actions?: BannerActionsProps[];
} & Pick<BannerCommonProps, 'title' | 'className' | 'hasIcon' | 'description'>) => {
  return (
    <Banner
      className={classNames(className)}
      description={description}
      title={title}
      hasIcon={hasIcon}
      isCloseable={false}
      type="danger"
      usage="inline"
      actions={actions}
    />
  );
};

const REFRESH_AND_SUPPORT_MESSAGE = 'Refresh the page and if the problem persists contact customer support.';
const descriptions = {
  default: `Failed to load organization data. ${REFRESH_AND_SUPPORT_MESSAGE}`,
  org_requires_mfa:
    'Your organization has required Multi Factor Authentication (MFA). Clicking Enable MFA below will log you out and you will then need to login using your email and password to configure MFA.',
};

export const ApiErrorBanner = ({
  error,
}: {
  error: FetchBaseQueryError | SerializedError | undefined;
} & Pick<BannerCommonProps, 'title' | 'className' | 'hasIcon'>) => {
  const [updateUserMfaEnabled] = consoleApi.useUpdateUserPreferenceMfaMutation();
  const { data: user } = consoleApi.useGetUserDetailsQuery();

  const handleUpdateMfaEnabled = () => {
    if (isNullish(user)) {
      return;
    }
    updateUserMfaEnabled({ userId: user.id, mfaEnabled: true })
      .unwrap()
      .then(() => {
        logout();
      })
      .catch((e: FetchBaseQueryError | SerializedError | undefined) => {
        const apiError = getApiError(e);
        if (isNotNullish(apiError.message)) {
          logger.error(apiError.message);
        }
        addNotification({
          type: 'danger',
          title: 'Failed to enable MFA',
          description: getErrorMessage(apiError),
          timeout: 5000,
        });
      });
  };

  if (isNotNullish(error)) {
    const apiError = getApiError(error);
    const errorBannerDict: Record<string, BannerProps> = {
      default: {
        title: 'Error',
        description: apiError.message ?? descriptions.default,
      },
      org_requires_mfa: {
        title: 'MFA Required',
        description: descriptions.org_requires_mfa,
        actions: [
          {
            as: 'button',
            label: 'Enable MFA',
            onClick() {
              handleUpdateMfaEnabled();
            },
            fill: 'outlined',
          },
        ],
      },
    };
    const reason = apiError.reason ?? 'default';
    return <ErrorBanner className="mx-4 my-4" {...errorBannerDict[reason]} />;
  }
  return undefined;
};
